import { put, takeLatest, call } from 'redux-saga/effects'
import { FORM_ERROR } from 'final-form'
import {
  API_URL_USER_PROFILE,
  API_URL_USER_PROFILE_UPDATE_PASSWORD,
  API_URL_USER_PROFILE_PHOTO,
  API_URL_REFRESH_TOKEN,
  RESET_STORE,
  API_URL_USER_PROFILE_UPDATE_MNA_FILTERS,
  API_URL_USER_PROFILE_UPDATE_LOGBOOK_FILTERS,
} from 'constants/index'
import fetchFromAPI, {
  getDefaultAutorizedHeaders,
  getDefaultHeaders,
  toFormData,
} from 'helpers/fetchFromApi'
import NoticeHelper from 'helpers/notice'
import decodeJWT from 'helpers/decodeJWT'
import UrlHelper from 'helpers/url'
import * as ApiRouter from 'constants/apiRouting'
import { PROFILE_NOTIFICATION_SETTINGS } from 'constants/apiRouting'
import { SHOW_NOTICE } from '../notice'
import { LOGIN_FAILURE, LOGIN_SUCCESS } from '../auth/reducer'
import {
  FETCH_PROFILE,
  FETCH_PROFILE_SUCCESS,
  FETCH_PROFILE_FAILURE,
  UPDATE_PROFILE,
  UPDATE_PROFILE_SUCCESS,
  UPDATE_PROFILE_MNA_FILTERS,
  UPDATE_PROFILE_LOGBOOK_FILTERS,
  UPDATE_PROFILE_LOGBOOK_FILTERS_SUCCESS,
  UPDATE_PHOTO,
  UPDATE_PHOTO_SUCCESS,
  UPDATE_PASSWORD,
  SET_LICENSEE_CONTEXT,
  SET_JWT_DATA,
  REFRESH_TOKEN,
  SET_SITE_CONTEXT,
  UPDATE_USER_LAST_APPLICATION_URL,
  UPDATE_USER_LAST_APPLICATION_URL_SUCCESS,
  SAVE_MY_JOBS_FILTERS,
  SAVE_JOBS_SORTING,
  SAVE_MY_JOBS_DATA_VIEWER_FILTERS,
  UPDATE_NOTIFICATION_SETTINGS,
  UPDATE_PROFILE_SITE_SUCCESS,
  SAVE_MANAGEMENT_UI_FILTERS,
  SAVE_MANAGEMENT_UI_FILTERS_SUCCESS,
  SAVE_LOOKUP_TABLES_FILTERS,
  SAVE_LOOKUP_TABLES_FILTERS_SUCCESS,
  SET_SITE,
  SAVE_SIGNATURE,
  DELETE_SIGNATURE,
  SIGNATURE_IS_SAVING,
  PROFILE_IS_FETCHING,
} from './reducer'

function* updateProfile({ payload, promise }) {
  const { data, successMessage } = payload

  try {
    let formErrors = null
    const value = yield call(fetchFromAPI, {
      data,
      headers: getDefaultAutorizedHeaders(),
      method: 'PUT',
      partUrl: API_URL_USER_PROFILE,
    })

    if (value === 204) {
      yield put({ type: FETCH_PROFILE })
      yield put({ type: UPDATE_PROFILE_SUCCESS, payload: value })
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(successMessage),
      })
    } else if (typeof value === 'object') {
      const { message, errors } = value

      if (message) {
        yield put({
          type: SHOW_NOTICE,
          notice: NoticeHelper.createErrorObj(message),
        })
      }

      if (errors) {
        formErrors = errors
      }
    }

    return yield call(promise.resolve, formErrors)
  } catch (error) {
    yield put({
      type: SHOW_NOTICE,
      notice: NoticeHelper.createErrorObj(error.toString()),
    })
  }
}

function* updateNotificationSettings({ payload, promise }) {
  const { data, successMessage } = payload

  try {
    let formErrors = null
    const value = yield call(fetchFromAPI, {
      data,
      headers: getDefaultAutorizedHeaders(),
      method: 'PUT',
      partUrl: PROFILE_NOTIFICATION_SETTINGS,
    })

    if (value === 204) {
      yield put({ type: FETCH_PROFILE })
      yield put({ type: UPDATE_PROFILE_SUCCESS, payload: value })
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(successMessage),
      })
    } else if (typeof value === 'object') {
      const { message, errors } = value

      if (message) {
        yield put({
          type: SHOW_NOTICE,
          notice: NoticeHelper.createErrorObj(message),
        })
      }

      if (errors) {
        formErrors = errors
      }
    }

    return yield call(promise.resolve, formErrors)
  } catch (error) {
    yield put({
      type: SHOW_NOTICE,
      notice: NoticeHelper.createErrorObj(error.toString()),
    })
  }
}

function* updatePassword(action) {
  const { payload, promise } = action

  try {
    const value = yield call(fetchFromAPI, {
      data: payload,
      headers: getDefaultAutorizedHeaders(),
      method: 'PUT',
      partUrl: API_URL_USER_PROFILE_UPDATE_PASSWORD,
    })
    if (!value || !value.error) {
      yield put({ type: FETCH_PROFILE })
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(value.message),
      })
    } else {
      yield call(promise.resolve, {
        error: true,
        [FORM_ERROR]: value.errors,
        errorString: value.errors,
      })
    }
  } catch (error) {
    console.error(error.toString())
  }
}

function* updatePhoto(action) {
  try {
    const value = yield call(fetchFromAPI, {
      data: toFormData({ photo: action.payload }),
      headers: getDefaultAutorizedHeaders({
        'Content-Type': 'multipart/form-data',
        'X-HTTP-Method-Override': 'PUT',
      }),
      method: 'POST',
      partUrl: API_URL_USER_PROFILE_PHOTO,
    })

    if (!value || !value.error) {
      yield put({ type: UPDATE_PHOTO_SUCCESS, payload: value })
      yield put({ type: FETCH_PROFILE })
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(value.message),
      })
    } else {
      console.error(value.errors)
    }
  } catch (error) {
    console.error(error.toString())
  }
}

function* updateUserLastApplicationUrl({ payload, promise }) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: 'PUT',
      partUrl: UrlHelper.create(ApiRouter.UPDATE_USER_APPLICATION),
      data: { lastApplicationUrl: payload.lastApplicationUrl },
    })

    if (response && !response.error) {
      yield put({
        type: UPDATE_USER_LAST_APPLICATION_URL_SUCCESS,
        payload: response.lastApplicationUrl,
      })
    } else {
      yield put({
        type: FETCH_PROFILE_FAILURE,
        error: response.errors,
      })
    }

    return yield call(promise.resolve, false)
  } catch (error) {
    throw new Error(`UNKNOW SITE CHANGE ERROR. DATA: ${error}`)
  }
}

function* setSite({ payload }) {
  try {
    const newSite = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${ApiRouter.SITE_LIST}/${payload}`,
    })

    if (newSite && !newSite.error) {
      const { id, name, siteGroups, timezone } = newSite

      yield put({
        type: SET_SITE,
        payload: {
          id,
          name,
          siteGroups,
          timezone,
        },
      })
    }
  } catch (error) {
    console.error('SET SITE ERROR', error)
  }
}

function* getUserProfile() {
  yield put({ type: PROFILE_IS_FETCHING, payload: true })

  try {
    const value = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: API_URL_USER_PROFILE,
    })

    if (!value || !value.error) {
      yield put({ type: FETCH_PROFILE_SUCCESS, payload: value })

      if (value?.myJobsFilters.site !== value?.site.id) {
        yield call(setSite, {
          payload: value.myJobsFilters.site,
        })
      }
    } else {
      yield put({ type: FETCH_PROFILE_FAILURE, error: value.errors })
    }
    yield put({ type: PROFILE_IS_FETCHING, payload: false })
  } catch (error) {
    yield put({ type: PROFILE_IS_FETCHING, payload: false })
    yield put({ type: FETCH_PROFILE_FAILURE, error: error.toString() })
  }
}

function* setLicenseeContext(action) {
  try {
    const value = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: 'PUT',
      partUrl: UrlHelper.create(
        ApiRouter.PROFILE_LICENSEE_CONTEXT,
        action.payload,
      ),
      data: {},
    })

    if (!value || !value.error) {
      yield put({ type: FETCH_PROFILE })
    } else {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(value.errors),
      })
    }
  } catch (error) {
    yield put({
      type: SHOW_NOTICE,
      notice: NoticeHelper.createErrorObj(error.toString()),
    })
  }
}

function* setSiteContext(action) {
  try {
    const value = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: 'PUT',
      partUrl: ApiRouter.PROFILE_SITE_CONTEXT,
      data: action.payload,
    })

    if (!value || !value.error) {
      yield put({ type: FETCH_PROFILE })
    } else {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(value.errors),
      })
    }
  } catch (error) {
    yield put({
      type: SHOW_NOTICE,
      notice: NoticeHelper.createErrorObj(error.toString()),
    })
  }
}

function* refreshToken() {
  try {
    const response = yield call(fetchFromAPI, {
      data: { refresh_token: localStorage.getItem('refresh_token') },
      headers: getDefaultHeaders(),
      method: 'POST',
      partUrl: API_URL_REFRESH_TOKEN,
    })

    if (!response || !response.error) {
      localStorage.setItem('token', response.token)
      yield put({ type: SET_JWT_DATA, payload: decodeJWT(response.token) })
      yield put({ type: LOGIN_SUCCESS })
    } else {
      yield put({ type: LOGIN_FAILURE, error: response.errors })
      yield put({ type: RESET_STORE })
    }
  } catch (error) {
    yield put({ type: LOGIN_FAILURE, error: error.toString() })
  }
}

function* updateProfileMnaFilters(action) {
  const { mna_filters } = action.payload

  try {
    yield call(fetchFromAPI, {
      data: {
        mna_filters: {
          ...mna_filters,
        },
      },
      headers: getDefaultAutorizedHeaders(),
      method: 'PUT',
      partUrl: API_URL_USER_PROFILE_UPDATE_MNA_FILTERS,
    })
  } catch (error) {
    yield put({
      type: SHOW_NOTICE,
      notice: NoticeHelper.createErrorObj(error.toString()),
    })
  }
}

function* updateProfileLogbookFilters({
  payload: { logbook_filters },
  promise: { resolve },
}) {
  try {
    const response = yield call(fetchFromAPI, {
      data: {
        logbook_filters: {
          ...logbook_filters,
        },
      },
      headers: getDefaultAutorizedHeaders(),
      method: 'PUT',
      partUrl: API_URL_USER_PROFILE_UPDATE_LOGBOOK_FILTERS,
    })

    if (response && !response.error) {
      yield put({
        type: UPDATE_PROFILE_LOGBOOK_FILTERS_SUCCESS,
        payload: response.logbookFilters,
      })

      if (resolve) {
        yield call(resolve, response)
      }
    } else {
      yield put({ type: FETCH_PROFILE_FAILURE, error: response.errors })
    }
  } catch (error) {
    yield put({
      type: SHOW_NOTICE,
      notice: NoticeHelper.createErrorObj(error.toString()),
    })
  }
}

function* saveMyJobsFilters({ payload, promise: { resolve } }) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: 'PUT',
      partUrl: ApiRouter.SET_MY_JOBS_FILTERS,
      data: payload,
    })

    if (response && !response.error) {
      yield put({
        type: UPDATE_PROFILE_SITE_SUCCESS,
        payload: response.myJobsFilters,
      })

      if (payload?.my_jobs_filters.site) {
        yield call(setSite, {
          payload: payload.my_jobs_filters.site,
        })
      }
    }

    if (resolve) {
      resolve(response)
    }
  } catch (error) {
    console.error('SET JOB FILTERS ERROR', error)
  }
}

function* saveJobsSorting({ payload, promise: { resolve } }) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: 'PUT',
      partUrl: ApiRouter.SET_JOBS_SORTING,
      data: payload,
    })

    if (resolve) {
      resolve(response)
    }
  } catch (error) {
    console.error('SET JOBS SORTING ERROR', error)
  }
}

function* saveMyJobsDataViewerFilters({ payload }) {
  try {
    yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: 'PUT',
      partUrl: ApiRouter.SET_MY_JOBS_DATA_VIEWER_FILTERS,
      data: payload,
    })

    // TODO: возможно тут надо что то сделать, а может и поебать
  } catch (error) {
    console.error('SET JOB DATA VIEWER FILTERS ERROR', error)
  }
}

function* saveManagementUIFilters({ payload, promise }) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: ApiRouter.SET_MANAGEMENT_UI_FILTERS,
      method: 'PUT',
      data: payload,
    })

    if (response && !response.error) {
      yield put({
        type: SAVE_MANAGEMENT_UI_FILTERS_SUCCESS,
        payload: response.jobsManagementFilters,
      })

      if (promise && promise.resolve) {
        promise.resolve()
      }
    }
  } catch (error) {
    console.error('SAVE FILTERS FAILURE ', error)
  }
}

function* saveLookupTablesFilters({ payload }) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: ApiRouter.SET_LOOKUP_TABLES_FILTERS,
      method: 'PUT',
      data: payload,
    })

    if (response && !response.error) {
      yield put({
        type: SAVE_LOOKUP_TABLES_FILTERS_SUCCESS,
        payload: response.lookupTableFilters,
      })
    }
  } catch (error) {
    console.error('SAVE FILTERS FAILURE ', error)
  }
}

function* saveSignature({ payload, promise }) {
  yield put({ type: SIGNATURE_IS_SAVING, payload: true })

  try {
    const { signature, successMessage } = payload
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: ApiRouter.SET_SIGNATURE,
      method: 'PUT',
      data: { signature },
    })

    if (response && !response.error) {
      yield put({ type: FETCH_PROFILE })
      yield put({ type: UPDATE_PROFILE_SUCCESS, payload: response })

      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(successMessage),
      })

      if (promise && promise.resolve) {
        promise.resolve()
      }
    }
    yield put({ type: SIGNATURE_IS_SAVING, payload: false })
  } catch (error) {
    yield put({ type: SIGNATURE_IS_SAVING, payload: false })
    console.error('SAVE SIGNATURE FAILURE ', error)
    yield put({
      type: SHOW_NOTICE,
      notice: NoticeHelper.createErrorObj(error.toString()),
    })
  }
}

function* deleteSignature({ payload, promise }) {
  yield put({ type: SIGNATURE_IS_SAVING, payload: true })

  try {
    const { successMessage } = payload
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: ApiRouter.SET_SIGNATURE,
      method: 'DELETE',
    })

    if (response === 204) {
      yield put({ type: FETCH_PROFILE })
      yield put({ type: UPDATE_PROFILE_SUCCESS, payload: response })
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(successMessage),
      })
      if (promise && promise.resolve) {
        promise.resolve()
      }
    } else if (typeof value === 'object') {
      const { message } = response

      if (message) {
        yield put({
          type: SHOW_NOTICE,
          notice: NoticeHelper.createErrorObj(message),
        })
      }
    }
    yield put({ type: SIGNATURE_IS_SAVING, payload: false })
  } catch (error) {
    yield put({ type: SIGNATURE_IS_SAVING, payload: false })

    console.error('DELETE SIGNATURE FAILURE ', error)
    yield put({
      type: SHOW_NOTICE,
      notice: NoticeHelper.createErrorObj(error.toString()),
    })
  }
}

function* mySaga() {
  yield takeLatest(UPDATE_PROFILE, updateProfile)
  yield takeLatest(UPDATE_NOTIFICATION_SETTINGS, updateNotificationSettings)
  yield takeLatest(SET_LICENSEE_CONTEXT, setLicenseeContext)
  yield takeLatest(SET_SITE_CONTEXT, setSiteContext)
  yield takeLatest(UPDATE_PHOTO, updatePhoto)
  yield takeLatest(FETCH_PROFILE, getUserProfile)
  yield takeLatest(LOGIN_SUCCESS, getUserProfile)
  yield takeLatest(UPDATE_PASSWORD, updatePassword)
  yield takeLatest(
    UPDATE_USER_LAST_APPLICATION_URL,
    updateUserLastApplicationUrl,
  )
  yield takeLatest(REFRESH_TOKEN, refreshToken)
  yield takeLatest(UPDATE_PROFILE_MNA_FILTERS, updateProfileMnaFilters)
  yield takeLatest(UPDATE_PROFILE_LOGBOOK_FILTERS, updateProfileLogbookFilters)
  yield takeLatest(SAVE_MY_JOBS_FILTERS, saveMyJobsFilters)
  yield takeLatest(SAVE_JOBS_SORTING, saveJobsSorting)
  yield takeLatest(
    SAVE_MY_JOBS_DATA_VIEWER_FILTERS,
    saveMyJobsDataViewerFilters,
  )
  yield takeLatest(SAVE_MANAGEMENT_UI_FILTERS, saveManagementUIFilters)
  yield takeLatest(SAVE_LOOKUP_TABLES_FILTERS, saveLookupTablesFilters)
  yield takeLatest(SAVE_SIGNATURE, saveSignature)
  yield takeLatest(DELETE_SIGNATURE, deleteSignature)
}

export default mySaga
